import React, { useState, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonFunctions,
} from '../../shared/Button';
import InlineHelpText from '../../shared/InlineHelpText';

import {
  submitText,
  cancelText,
  staffAccessLevelsDescriptions,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import { Condition } from '../../shared/Condition';

export function UnconnectedUserStaffProfileOptions(props) {
  const { user, onSubmit, previous } = props;
  const navigate = useNavigate();
  
  // Add fallback for previous prop
  const handleCancel = () => {
    navigate(previous || '/users/');
  };

  const [
    showCheckInReportsEmailHelpText,
    setShowCheckInReportsEmailHelpText,
  ] = useState(false);
  function toggleShowCheckInReportsEmailHelpText(value) {
    setShowCheckInReportsEmailHelpText(value);
  }
  const checkInReportsEmailHelpTextRef = useRef();
  const checkInReportsEmailHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.checkInReportsEmailHelpText"
      defaultMessage="This user will receive emails for check-ins."
    />
  );

  const [
    showDisplayPanicButtonsHelpText,
    setShowDisplayPanicButtonsHelpText,
  ] = useState(false);

  function toggleDisplayPanicButtonsHelpText(value) {
    setShowDisplayPanicButtonsHelpText(value);
  }
  const displayPanicButtonsHelpTextRef = useRef();
  const displayPanicButtonsHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.displayPanicButtonsHelpText"
      defaultMessage="User will have dashboard panic buttons displayed that can be used to trigger alarms."
    />
  );

  const [
    showStatusCheckinHelpText,
    setShowStatusCheckinHelpText,
  ] = useState(false);

  function toggleStatusCheckinHelpText(value) {
    setShowStatusCheckinHelpText(value);
  }
  const statusCheckinHelpTextRef = useRef();
  const statusCheckinHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.statusCheckinHelpText"
      defaultMessage="This user is allowed to edit User Status and check-in exclusion settings."
    />
  );
  const [
    showPanicButtonTitleHelpText,
    setShowPanicButtonTitleHelpText,
  ] = useState(false);

  function togglePanicButtonTitleHelpText(value) {
    setShowPanicButtonTitleHelpText(value);
  }
  const panicButtonTitleHelpTextRef = useRef();
  const panicButtonTitleHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.panicButtonTitleHelpText"
      defaultMessage="The label on the dashboard panic button for this user."
    />
  );

  const [showAccessLevelHelpText, setShowAccessLevelHelpText] = useState(false);
  function toggleAccessLevelHelpText(value) {
    setShowAccessLevelHelpText(value);
  }
  const accessLevelHelpTextRef = useRef();

  const [showDisplayModeHelpText, setShowDisplayModeHelpText] = useState(false);
  function toggleDisplayModeHelpText(value) {
    setShowDisplayModeHelpText(value);
  }
  const displayModeHelpTextRef = useRef();
  const displayModeHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.displayModeHelpText"
      defaultMessage="This is the display mode for this user."
      description="Help text for understanding what the access level is."
    />
  );

  const [showRefreshRateHelpText, setShowRefreshRateHelpText] = useState(false);
  function toggleRefreshRateHelpText(value) {
    setShowRefreshRateHelpText(value);
  }
  const refreshRateHelpTextRef = useRef();
  const refreshRateHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.refreshRateHelpText"
      defaultMessage="This is how frequent, in seconds, the dashboard refreshes for the user."
    />
  );

  const [
    showRestrictConfirmationOfAlarmsHelpText,
    setRestrictConfirmationOfAlarmsHelpText,
  ] = useState(false);
  function toggleShowResctrictConfirmationOfAlarmsHelpText(value) {
    setRestrictConfirmationOfAlarmsHelpText(value);
  }
  const restrictConfirmationOfAlarmsHelpTextRef = useRef();
  const restrictConfirmationOfAlarmsHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.restrictConfirmationOfAlarmsHelpText"
      defaultMessage="This user is restricted from confirming alarms in the SARA dashboard screen."
    />
  );

  const [
    showDailyAdminReportHelpText,
    setShowDailyAdminReportHelpText,
  ] = useState(false);
  function toggleShowDailyAdminReportHelpText(value) {
    setShowDailyAdminReportHelpText(value);
  }
  const dailyAdminReportHelpTextRef = useRef();
  const dailyAdminReportHelpText = (
    <FormattedMessage
      id="userStaffProfileOptions.dailyAdminReportHelpText"
      defaultMessage="This user will receive the daily admin report which also includes low battery alarms."
      description="Help text for understanding what the user check-in exclude means."
    />
  );
  const pref = user.preferences;
  let userInfo = {
    show_panic_buttons: false,
  };
  if (pref) {
    userInfo = {
      user: pref.user,
      access_level: pref.access_level,
      display_mode: pref.display_mode,
      checkin_reports: pref.checkin_reports,
      disallow_web_alarm_confirm: pref.disallow_web_alarm_confirm,
      admin_reports: pref.admin_reports,
      refresh_rate: pref.refresh_rate,
      show_panic_buttons: pref.show_panic_buttons,
      panic_title: pref.panic_title,
      edit_status_checkin_exclusion: pref.edit_status_checkin_exclusion,
    };
  } else {
    userInfo = {
      user: user.id,
    };
  }

  return (
    <div className="content-display__container">
      <Form
        data-test="staff-preferences-form"
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          ...userInfo,
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form onSubmit={handleSubmit} className="width-100">
            <Field
              className="visually-hidden"
              name="user"
              component="input"
              type="text"
            />
            <div className="content-display__container">
              <div className="content-display__column content-display__column">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="text-large text-light text-deep-blue">
                      <FormattedMessage
                        id="userStaffProfileOptions.accessLevelText"
                        defaultMessage="Access Level"
                        description="The access level for a user"
                      />
                    </label>
                    <AccessControl
                      data-test="access-control"
                      feature={permissions.static.users.editUserCheckinProfile}
                      permissionMessage={true}
                      ifNotAllowed={
                        <Field
                          className="form-item__field"
                          name="access_level"
                          component="select"
                          data-test="access-level-disabled"
                          disabled={true}>
                          <FormattedMessage
                            id="userStaffProfileOptions.readOnly"
                            defaultMessage="Read Only"
                            description="Read Only">
                            {(txt) => <option value="0">{txt}</option>}
                          </FormattedMessage>
                          <FormattedMessage
                            id="userStaffProfileOptions.basicAccess"
                            defaultMessage="Basic Access"
                            description="Basic Access">
                            {(txt) => <option value="1">{txt}</option>}
                          </FormattedMessage>
                          <FormattedMessage
                            id="userStaffProfileOptions.reports"
                            defaultMessage="Reports"
                            description="Reports">
                            {(txt) => <option value="4">{txt}</option>}
                          </FormattedMessage>
                          <FormattedMessage
                            id="userStaffProfileOptions.deviceAdministration"
                            defaultMessage="Device Administration"
                            description="Device Administration">
                            {(txt) => <option value="6">{txt}</option>}
                          </FormattedMessage>
                          <FormattedMessage
                            id="userStaffProfileOptions.systemAdministration"
                            defaultMessage="System Administration"
                            description="System Administration">
                            {(txt) => <option value="8">{txt}</option>}
                          </FormattedMessage>
                          <FormattedMessage
                            id="userStaffProfileOptions.userAdministration"
                            defaultMessage="User Administration"
                            description="User Administration">
                            {(txt) => <option value="10">{txt}</option>}
                          </FormattedMessage>
                        </Field>
                      }>
                      <Field
                        className="form-item__field"
                        name="access_level"
                        data-test="access-level"
                        component="select"
                        // This will allow user to submit without changing access level
                        defaultValue="0">
                        <FormattedMessage
                          id="userStaffProfileOptions.readOnly"
                          defaultMessage="Read Only"
                          description="Read Only">
                          {(txt) => <option value="0">{txt}</option>}
                        </FormattedMessage>
                        <FormattedMessage
                          id="userStaffProfileOptions.basicAccess"
                          defaultMessage="Basic Access"
                          description="Basic Access">
                          {(txt) => <option value="1">{txt}</option>}
                        </FormattedMessage>
                        <FormattedMessage
                          id="userStaffProfileOptions.reports"
                          defaultMessage="Reports"
                          description="Reports">
                          {(txt) => <option value="4">{txt}</option>}
                        </FormattedMessage>
                        <FormattedMessage
                          id="userStaffProfileOptions.deviceAdministration"
                          defaultMessage="Device Administration"
                          description="Device Administration">
                          {(txt) => <option value="6">{txt}</option>}
                        </FormattedMessage>
                        <FormattedMessage
                          id="userStaffProfileOptions.systemAdministration"
                          defaultMessage="System Administration"
                          description="System Administration">
                          {(txt) => <option value="8">{txt}</option>}
                        </FormattedMessage>
                        <FormattedMessage
                          id="userStaffProfileOptions.userAdministration"
                          defaultMessage="User Administration"
                          description="User Administration">
                          {(txt) => <option value="10">{txt}</option>}
                        </FormattedMessage>
                      </Field>
                    </AccessControl>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        accessLevelHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showAccessLevelHelpText}
                    />
                  </div>
                </div>
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="text-large text-light text-deep-blue">
                      <FormattedMessage
                        id="userStaffProfileOptions.displayModeText"
                        defaultMessage="Display Mode"
                        description="Set the display mode for this user"
                      />
                    </label>
                    <Field
                      className="form-item__field"
                      name="display_mode"
                      component="select">
                      <FormattedMessage
                        id="userStaffProfileOptions.showAllMonitoredDevices"
                        defaultMessage="Show all monitored devices"
                        description="Show all monitored devices">
                        {(txt) => <option value="1">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id="userStaffProfileOptions.showOnlyActiveAlerts"
                        defaultMessage="Show only active/recent alerts"
                        description="Show only active/recent alerts">
                        {(txt) => <option value="2">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id="userStaffProfileOptions.showQuickMessagScreen"
                        defaultMessage="Show quick message screen"
                        description="Show quick message screen">
                        {(txt) => <option value="4">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id="userStaffProfileOptions.showManualAlarmScreen"
                        defaultMessage="Show manual alarm screen"
                        description="Show manual alarm screen">
                        {(txt) => <option value="5">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id="userStaffProfileOptions.showLowBatteryDevices"
                        defaultMessage="Show low battery devices"
                        description="Show low battery devices">
                        {(txt) => <option value="6">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id= "userStaffProfileOptions.showTamperDevices"
                        defaultMessage="Show tamper devices"
                        description="Show tamper devices">
                        {(txt) => <option value="7">{txt}</option>}
                      </FormattedMessage>
                      <FormattedMessage
                        id= "userStaffProfileOptions.showDormantDevices"
                        defaultMessage="Show dormant devices"
                        description="Show dormant devices">
                        {(txt) => <option value="8">{txt}</option>}
                      </FormattedMessage>
                    </Field>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        displayModeHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showDisplayModeHelpText}
                    />
                  </div>
                </div>
              </div>
              {["1", "4", "6", "8", 1, 4, 6, 8].includes(values.access_level) && (
                <div className="content-display__column content-display__column">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <Field
                        name="edit_status_checkin_exclusion"
                        component="input"
                        type="checkbox"
                        className="visually-hidden"
                        id="edit_status_checkin_exclusion"
                      />
                      <label
                        className="text-large text-light text-deep-blue form-item__field-toggle"
                        htmlFor="edit_status_checkin_exclusion">
                        <FormattedMessage
                          id="userStaffProfileOptions.statusCheckinOptionText"
                          defaultMessage="Allow editing User status and check-ins exclusions"
                          description="Allow user to change resident status and exclude from check-ins"
                        />
                      </label>
                    </div>
                    <div className="item-container__info-container">
                      <HelpIcon
                        styles="margin-left-5"
                        onClick={() =>
                          statusCheckinHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showStatusCheckinHelpText}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="content-display__column content-display__column">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <Field
                      name="checkin_reports"
                      id="checkin_reports"
                      component="input"
                      type="checkbox"
                      className="visually-hidden"
                    />
                    <label
                      className="text-large text-light text-deep-blue form-item__field-toggle"
                      htmlFor="checkin_reports">
                      <FormattedMessage
                        id="userStaffProfileOptions.checkInReportsEmailText"
                        defaultMessage="This user will receive check-in report emails"
                        description="This user will receive check-in report emails"
                      />
                    </label>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        checkInReportsEmailHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showCheckInReportsEmailHelpText}
                    />
                  </div>
                </div>
              </div>
              <div className="content-display__column content-display__column">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <AccessControl
                      data-test="access-control"
                      feature={permissions.static.users.editUserCheckinProfile}
                      ifNotAllowed={
                        <Field
                          name="disallow_web_alarm_confirm"
                          component="input"
                          type="checkbox"
                          className="visually-hidden"
                          disabled={true}
                          id="disallow_web_alarm_confirm"
                          data-test="disallow-web-alarm-confirm-disabled"
                        />
                      }
                      permissionMessage={true}>
                      <Field
                        name="disallow_web_alarm_confirm"
                        component="input"
                        type="checkbox"
                        className="visually-hidden"
                        id="disallow_web_alarm_confirm"
                        data-test="disallow-web-alarm-confirm"
                      />
                    </AccessControl>
                    <label
                      className="text-large text-light text-deep-blue form-item__field-toggle"
                      htmlFor="disallow_web_alarm_confirm">
                      <FormattedMessage
                        id="userStaffProfileOptions.disallowWebAlarmConfirmText"
                        defaultMessage="This user is not allowed to confirm alarms from the SARA dashboard"
                        description="Set the display mode for this user"
                      />
                    </label>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5 keep-size"
                      onClick={() =>
                        restrictConfirmationOfAlarmsHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showRestrictConfirmationOfAlarmsHelpText}
                    />
                  </div>
                </div>
              </div>
              <div className="content-display__column content-display__column">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <Field
                      name="admin_reports"
                      component="input"
                      type="checkbox"
                      className="visually-hidden"
                      id="admin_reports"
                    />
                    <label
                      className="text-large text-light text-deep-blue form-item__field-toggle"
                      htmlFor="admin_reports">
                      <FormattedMessage
                        id="userStaffProfileOptions.dailyAdminReportText"
                        defaultMessage="This user will receive the daily admin report"
                        description="Set the display mode for this user"
                      />
                    </label>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        dailyAdminReportHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showDailyAdminReportHelpText}
                    />
                  </div>
                </div>
              </div>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <Field
                      name="show_panic_buttons"
                      component="input"
                      type="checkbox"
                      className="visually-hidden"
                      id="show_panic_buttons"
                    />
                    <label
                      className="text-large text-light text-deep-blue form-item__field-toggle"
                      htmlFor="show_panic_buttons">
                      <FormattedMessage
                        id="userStaffProfileOptions.displayPanicButtonsText"
                        defaultMessage="Show dashboard panic buttons"
                        description="Show the dashboard panic buttons"
                      />
                    </label>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        displayPanicButtonsHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showDisplayPanicButtonsHelpText}
                    />
                  </div>
                </div>
              </div>
              <Condition when="show_panic_buttons" is={false}>
                <div className="content-display__column content-display__column--1-2"></div>
              </Condition>

              <Condition when="show_panic_buttons" is={true}>
                <div className="content-display__column content-display__column--1-2">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <label className="text-large text-light text-deep-blue">
                        <FormattedMessage
                          id="userStaffProfileOptions.panicButtonTitleText"
                          defaultMessage="Panic Button Title:"
                          description="The label on the dashboard panic button for this user"
                        />
                      </label>
                      <Field
                        className="form-item__field"
                        name="panic_title"
                        component="input"
                        type="text"
                      />
                    </div>
                    <div className="item-container__info-container">
                      <HelpIcon
                        styles="margin-left-5"
                        onClick={() =>
                          panicButtonTitleHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showPanicButtonTitleHelpText}
                      />
                    </div>
                  </div>
                </div>
              </Condition>
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label className="text-large text-light text-deep-blue">
                      <FormattedMessage
                        id="userStaffProfileOptions.refreshRateText"
                        defaultMessage="Refresh Rate"
                        description="The frequency of the dashboard refreshing"
                      />
                    </label>
                    <div className="">
                      <Field
                        name="refresh_rate"
                        component="input"
                        type="text"
                        className="margin-right-10"
                      />
                      <FormattedMessage
                        id="userStaffProfileOptions.seconds"
                        defaultMessage="seconds"
                        description="The seconds unit of time"
                      />
                    </div>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        refreshRateHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showRefreshRateHelpText}
                    />
                  </div>
                </div>
              </div>
              <div className="content-display__column content-display__column--1-2"></div>
              <div className="width-100 margin-bottom-10">
                <InlineHelpText
                  ref={accessLevelHelpTextRef}
                  toggleFunction={toggleAccessLevelHelpText}
                  message={staffAccessLevelsDescriptions}
                />
                <InlineHelpText
                  ref={displayModeHelpTextRef}
                  toggleFunction={toggleDisplayModeHelpText}
                  message={displayModeHelpText}
                />
                <InlineHelpText
                  ref={checkInReportsEmailHelpTextRef}
                  toggleFunction={toggleShowCheckInReportsEmailHelpText}
                  message={checkInReportsEmailHelpText}
                />

                <InlineHelpText
                  ref={restrictConfirmationOfAlarmsHelpTextRef}
                  toggleFunction={
                    toggleShowResctrictConfirmationOfAlarmsHelpText
                  }
                  message={restrictConfirmationOfAlarmsHelpText}
                />
                <InlineHelpText
                  ref={dailyAdminReportHelpTextRef}
                  toggleFunction={toggleShowDailyAdminReportHelpText}
                  message={dailyAdminReportHelpText}
                />
                <InlineHelpText
                  ref={refreshRateHelpTextRef}
                  toggleFunction={toggleRefreshRateHelpText}
                  message={refreshRateHelpText}
                />
                <InlineHelpText
                  ref={displayPanicButtonsHelpTextRef}
                  toggleFunction={toggleDisplayPanicButtonsHelpText}
                  message={displayPanicButtonsHelpText}
                />
                <InlineHelpText
                  ref={statusCheckinHelpTextRef}
                  toggleFunction={toggleStatusCheckinHelpText}
                  message={statusCheckinHelpText}
                />

                <InlineHelpText
                  ref={panicButtonTitleHelpTextRef}
                  toggleFunction={togglePanicButtonTitleHelpText}
                  message={panicButtonTitleHelpText}
                />
                <hr />
                <div className="form__section-container form__section-container--spread">
                  <div className="form__items-group-container">
                    <button
                      type="button"
                      className="link link-text"
                      onClick={handleCancel}
                      data-test="staff-preferences-form-cancel-button">
                      {cancelText}
                    </button>
                  </div>
                  <div className="form__items-group-container">
                    <Button
                      buttonFunction={buttonFunctions.submit}
                      text={submitText}
                      buttonType={buttonTypes.success}
                      buttonSize={buttonSizes.small}
                      additionalStyles={'no-margin margin-left-15'}
                      disabled={pristine || invalid}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default UnconnectedUserStaffProfileOptions;
